import { ChakraProvider, Flex, Spinner, useColorMode } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
    BrowserRouter,
    Redirect,
    Route,
    Switch,
    useHistory
} from 'react-router-dom';
import Layout from './components/layout';
import { useGetContext } from './hooks';
import { SessionProvider } from './hooks/useSession';

import './index.css';

import BestPrice from './pages/best-price/best-price';
import BetSplit from './pages/best-price/bet-split';
import BetPlacePage from './pages/bet-place/bet-place-page';
import BetPlaceStandalonePage from './pages/bet-place/bet-place-standalone-page';
import Error from './pages/book-link/error';
import LinkRootBook from './pages/book-link/link-root-book';
import Done from './pages/done';
import AccountInfo from './pages/manage-accounts/account-info';
import OTPEntry from './pages/manage-accounts/otp-entry-page';
import theme from './styles/theme';

/*
|--------------------------------------------------------------------------
| URL Paths
|--------------------------------------------------------------------------
*/

export enum PageUrls {
    BOOK_ROOT = '/link/:cid',
    SECURITY_MESSAGE = '/link/:cid/security-message', // make paths vs root
    LOCATION_SELECT = '/link/:cid/books/location-select',
    BOOK_LIST = '/link/:cid/books/:state/list',
    LOGIN_FORM = '/link/:cid/region/:bookRegionId/login', // TODO: rename to BOOK_LOGIN_FORM
    LINK_SUCCESS = '/link/:cid/success/:state/:bookRegionId/:bookName',
    PrivacyPolicy = 'https://app.sharpsports.io/privacypolicy',
    TermsOfService = 'https://www.sharpsports.io/terms-of-service',
    BET_PLACE = '/place/:cid',
    BET_PLACE_STANDALONE = '/place/:marketSelectionId/:bookId',
    STANDALONE_PARLAY = '/place/parlay/:bookId',
    ManagementSession = '/link/:cid/management-session',
    BEST_PRICE = '/best-price/:cid',
    BET_SPLIT = '/best-price/:cid/bet-split',
    OTP_ENTRY = '/otp/:cid',
    EXTENSIONS_ROOT = '/extensions',
    DONE = '/done',
}

export const getLocationSelectPagePath = (cid: string): string => {
    return PageUrls.LOCATION_SELECT.replace(':cid', cid);
};

export const getBookListPagePath = (cid: string, state: string): string => {
    return PageUrls.BOOK_LIST.replace(':cid', cid).replace(':state', state);
};

export const getLoginFormPagePath = (cid: string, bookRegionId: any) => {
    return PageUrls.LOGIN_FORM.replace(':cid', cid).replace(
        ':bookRegionId',
        bookRegionId
    );
};

export const getRootPagePath = (cid: string) => {
    return PageUrls.BOOK_ROOT.replace(':cid', cid);
}

export const getBestPricePagePath = (cid: string) => {
    return PageUrls.BEST_PRICE.replace(':cid', cid);
};

export const getLoginSuccessPagePath = (
    cid: string,
    state: string,
    bookRegionId: string,
    bookName: string
) => {
    return PageUrls.LINK_SUCCESS.replace(':cid', cid)
        .replace(':bookRegionId', bookRegionId)
        .replace(':state', state)
        .replace(':bookName', bookName);
};

export const getSessionManagementPagePath = (cid: string) => {
    return PageUrls.ManagementSession.replace(':cid', cid);
};

export const getSecurityMessagePagePath = (cid: string) => {
    return PageUrls.SECURITY_MESSAGE.replace(':cid', cid);
};

export const getOTPEntryPagePath = (cid: string) => {
    return PageUrls.OTP_ENTRY.replace(':cid', cid);
};

/*
|--------------------------------------------------------------------------
| Routes
|--------------------------------------------------------------------------
*/

export function LoadingScreen() {
    const { colorMode } = useColorMode();
    return (
        <Layout colorMode={colorMode}>
            <Flex align="center" justify="center" w="full" h="100vh">
                <Spinner />
            </Flex>
        </Layout>
    );
}

interface IRouteProps {
    error?: boolean;
    loading?: boolean;
}

function MainRoute(props: IRouteProps) {
    const { loading, error } = props;
    if (error) {
        return <Error />;
    } else if (loading) {
        return <LoadingScreen />;
    } else {
        return (
            <Switch>
                <Route exact path={PageUrls.DONE} component={Done} />
                <Redirect
                    exact
                    from={PageUrls.BOOK_ROOT}
                    to={PageUrls.SECURITY_MESSAGE}
                />
                <Route
                    path={PageUrls.ManagementSession}
                    component={AccountInfo}
                    exact
                />
                <Route path={PageUrls.OTP_ENTRY} component={OTPEntry} exact />
                <Route
                    path={`${PageUrls.BOOK_ROOT}*`}
                    component={LinkRootBook}
                />
                <Route
                    exact
                    path={`${PageUrls.BEST_PRICE}`}
                    component={BestPrice}
                />
                <Route
                    exact
                    path={`${PageUrls.BET_SPLIT}`}
                    component={BetSplit}
                />
                <Route
                    exact
                    path={PageUrls.BET_PLACE}
                    component={BetPlacePage}
                />
                <Route
                    exact
                    path={PageUrls.BET_PLACE_STANDALONE}
                    component={BetPlaceStandalonePage}
                />
                <Route
                    exact
                    path={PageUrls.STANDALONE_PARLAY}
                    component={BetPlaceStandalonePage}
                />
            </Switch>
        );
    }
}

function App() {
    const { push } = useHistory();
    let standaloneRgx = /^\/place\/(MRKT_[0-9a-zA-Z]+\/(BOOK_[0-9a-zA-Z]+|[a-z]{2})|parlay\/(BOOK_[0-9a-zA-Z]+|[a-z]{2}))$/;
    const contextExpected = !(standaloneRgx.test(location.pathname) || location.pathname.includes('/done'));

    //ideally we only load these vars if we are expecting a context
    const { data, loading, error, fetchContext, setContext } = useGetContext();

    //Allow for testing userAgent to be passed by query param
    const [testUserAgent, setTestUserAgent] = useState<string | null>(null);
    const [userTheme, setUserTheme] = useState(theme)
    const queryParams = new URLSearchParams(location.search);

    const setSwalTheme = (uiMode: string, primaryColor: string, secondaryColor: string) => {

        if (uiMode == 'light'){
            document.documentElement.style.setProperty('--background-color', '#FFFFFF');
            document.documentElement.style.setProperty('--font-color', '#24356A');
        } else if (uiMode == 'dark'){
            document.documentElement.style.setProperty('--background-color', '#24356A');
            document.documentElement.style.setProperty('--font-color', '#FFFFFF');
        }
        document.documentElement.style.setProperty('--primary-color', primaryColor);
        document.documentElement.style.setProperty('--secondary-color', secondaryColor);
    }

    useEffect(() => {
        if (!testUserAgent) {
            setTestUserAgent(queryParams.get('userAgent'));
        }
    }, []);

    useEffect(() => {
        //if mg session and bettorAccounts we want to show mg page instead of security page
        if (!loading && !!data?.user) {
            if (
                data?.user?.managementSession &&
                !!data.bettorAccounts?.length
            ) {
                if (
                    window.location.pathname ==
                    `/link/${data.cid}/security-message`
                ) {
                    push(getSessionManagementPagePath(data.cid));
                }
            }

        }
        const updatedTheme = {
            ...theme,
            colors: {
              ...theme.colors,
              primary: data?.user?.primaryColor,
              secondary: data?.user?.secondaryColor
            },
          };

        setUserTheme(updatedTheme)

        setSwalTheme(
            data?.user?.uiMode || '',
            data?.user?.primaryColor || '',
            data?.user?.secondaryColor || ''
        )

    }, [loading, data]);

    if (!contextExpected) {
        return (
            <ChakraProvider theme={userTheme}>
                <MainRoute loading={loading} error={false} />
            </ChakraProvider>
        );
    } else {
        return (
            <SessionProvider
                value={{
                    ...data,
                    loading,
                    fetchContext,
                    setContext,
                    testUserAgent,
                }}
            >
                <ChakraProvider theme={userTheme}>
                    <MainRoute loading={loading} error={!loading && !data} />
                </ChakraProvider>
            </SessionProvider>
        );
    }
}

export default function () {
    return (
        <BrowserRouter>
            <App />
        </BrowserRouter>
    );
}
